import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Circle } from '@phosphor-icons/react';

const Software = ({ products = [] }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-100px' });

    // Default placeholder products if none exist
    const defaultProducts = [
        {
            id: '1',
            name: 'Survey Tool',
            description: 'Professional marine survey management application for vessel inspections and reporting.',
            url: 'https://surveytool.spindrift-eng.com',
            status: 'coming_soon'
        },
        {
            id: '2',
            name: 'Engineering Calculator',
            description: 'Comprehensive calculation tools for marine engineering applications and structural analysis.',
            url: '#',
            status: 'coming_soon'
        },
        {
            id: '3',
            name: 'Fleet Manager',
            description: 'Vessel fleet management and maintenance scheduling platform for maritime operations.',
            url: '#',
            status: 'coming_soon'
        }
    ];

    const displayProducts = products.length > 0 ? products : defaultProducts;

    const getStatusBadge = (status) => {
        switch(status) {
            case 'active':
                return { text: 'Live', className: 'bg-green-500/20 text-green-400' };
            case 'deprecated':
                return { text: 'Deprecated', className: 'bg-red-500/20 text-red-400' };
            default:
                return { text: 'Coming Soon', className: 'bg-secondary/20 text-secondary' };
        }
    };

    return (
        <section 
            id="software" 
            className="py-24 sm:py-32 bg-background-base"
            data-testid="software-section"
            ref={ref}
        >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <span className="text-sm uppercase tracking-[0.2em] font-bold text-secondary">
                        Spindrift Engineering Software
                    </span>
                    <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-text-primary mt-4">
                        Our Products
                    </h2>
                    <p className="text-text-secondary mt-4 max-w-2xl mx-auto">
                        Purpose-built applications designed to streamline operations in the marine and engineering industries.
                    </p>
                </motion.div>

                {/* Products List */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="space-y-4"
                >
                    {displayProducts.map((product) => {
                        const status = getStatusBadge(product.status);
                        const isClickable = product.status === 'active' && product.url && product.url !== '#';
                        
                        // Ensure URL has protocol
                        const fullUrl = product.url && !product.url.startsWith('http') 
                            ? `https://${product.url}` 
                            : product.url;
                        
                        const content = (
                            <div className="flex items-start gap-4 p-4 bg-background-surface border border-border-subtle rounded-sm hover:border-border-active transition-colors">
                                <Circle size={8} weight="fill" className="text-secondary mt-2 flex-shrink-0" />
                                <div className="flex-grow min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h3 className="text-text-primary font-medium">
                                            {product.name}
                                        </h3>
                                        <span className={`px-2 py-0.5 text-xs rounded ${status.className}`}>
                                            {status.text}
                                        </span>
                                    </div>
                                    <p className="text-text-secondary text-sm mt-1">
                                        {product.description}
                                    </p>
                                    {product.url && product.url !== '#' && (
                                        <p className="text-text-muted text-xs font-mono mt-2">
                                            {product.url.replace('https://', '')}
                                        </p>
                                    )}
                                </div>
                                {isClickable && (
                                    <ArrowRight size={18} className="text-text-muted flex-shrink-0 mt-1" />
                                )}
                            </div>
                        );

                        return isClickable ? (
                            <a
                                key={product.id}
                                href={fullUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                                data-testid={`software-product-${product.id}`}
                            >
                                {content}
                            </a>
                        ) : (
                            <div key={product.id} data-testid={`software-product-${product.id}`}>
                                {content}
                            </div>
                        );
                    })}
                </motion.div>

                {/* More Coming Soon */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-center mt-8"
                >
                    <p className="text-text-muted text-sm">
                        More applications are currently in development.
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default Software;
